'use client';

import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { showsClient, applyPricingCoefficients, type PricingCoefficients } from '@bip/api-client';
import { useCurrency } from '../../../_components/CurrencyProvider';
import { ShowsLoadingOverlay } from '../ShowsLoadingOverlay';
import { SHOWS_BASE_PATH } from '../../config';

/**
 * Real `LTD.Calendar` vendor widget integration — per
 * https://developer.londontheatredirect.com/documentation/widgets/calendar
 * (fetched and spike-tested live 2026; see the doc comment on
 * `ShowDetailRecord.event.ltdEventUrlId` in `show-detail-types.ts` for the
 * full write-up of what was confirmed).
 *
 * This REPLACES the earlier from-scratch `AvailabilityCalendar` +
 * `ShowtimeList` + `TicketQuantityStepper` reproduction on the Show Detail
 * Page (that trio is still used, unchanged, on the Seat Selection Page —
 * this component is Show-Detail-Page-specific).
 *
 * Ticket-quantity +/- stepper: the documented `LTD.Calendar` public API
 * (`init()` + the single `LTD.Calendar.toggle` DOM event) does NOT render
 * one itself — confirmed both from the docs and by inspecting the live
 * widget's own rendered DOM. The stepper visible on the REAL production
 * London Theatre Direct site is the CMS's OWN markup, imperatively
 * DOM-injected above the widget's content after it renders (see
 * `theaters-cms-reference-code/client-js/theatre-detail-page.js`
 * `injectTicketCounter()`). We reproduce the same visual result the
 * React way instead — `TicketQuantityHeader` below renders as an ordinary
 * sibling ABOVE the vendor mount point, rather than reaching into DOM the
 * vendor script owns. It does not (yet) feed the count into the vendor's
 * own "Select" links — see that component's doc comment.
 */

const LTD_CALENDAR_SCRIPT_SRC = 'https://calendar.finale-cdn.uk/latest/calendar.js';

/**
 * Last-resort fallback only — the real affiliate ID
 * (`4a1f1eb8-12ef-4285-b32a-ead889899511`, the same one the legacy CMS
 * hardcodes directly in its production client JS) is already provisioned
 * via `NEXT_PUBLIC_SHOWS_LTD_AFFILIATE_ID` in `apps/web/.env.local` (mirrors
 * `SHOWS_LTD_AFFILIATE_ID` in `services/.env` — see `DOCS/shows/plan.md`
 * §9.10), so normal runs never hit this constant. It only applies if that
 * env var is ever unset (e.g. a fresh checkout without `.env.local`), in
 * which case LTD's OFFICIAL PUBLIC SANDBOX id renders one fixed canned demo
 * dataset regardless of `eventUrlId` (confirmed live) rather than failing
 * outright.
 */
const LTD_SANDBOX_AFFILIATE_ID = '00000000-0000-0000-0000-000000000000';

/**
 * `LTD.Calendar` has no documented "ready"/"error" event (unlike its
 * sibling `LTD.SeatPlan`, which fires a documented `OnError` — see
 * `LtdSeatPlanWidget.tsx`). This timeout is our own best-effort heuristic
 * for "the script loaded but nothing ever rendered into the root element"
 * — not a vendor-documented contract.
 */
const RENDER_TIMEOUT_MS = 12_000;

type WidgetState = 'empty' | 'loading' | 'ready' | 'error';

type LtdCalendarGlobal = {
  LTD?: { Calendar?: { init: (opts: Record<string, unknown>) => void } };
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Removes any past-month `<option>` from the widget's own month `<select>`.
 * Dynamic: only filters out past months relative to today (does not hardcode any future cutoff year).
 * Matches on the option's rendered text ("{MonthName} {Year}", confirmed via the live widget's own DOM)
 * rather than its `value`. Safe to call repeatedly.
 */
function pruneMonthOptions(root: HTMLElement) {
  const select = root.querySelector('select');
  if (!select) return;
  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();

  Array.from(select.options).forEach((opt) => {
    const m = /^(\w+)\s+(\d{4})$/.exec((opt.textContent || '').trim());
    if (!m) return;
    const optMonth = MONTH_NAMES.indexOf(m[1]);
    const optYear = Number(m[2]);
    if (optMonth < 0) return;

    const isPast = optYear < currentYear || (optYear === currentYear && optMonth < currentMonth);
    if (isPast) opt.remove();
  });
}

/**
 * Client-side price-currency rewrite — ported from the legacy CMS's own
 * approach (`theatre-detail-page.js`'s `data-rate`/`data-symbol` +
 * MutationObserver price rewrite). `LTD.Calendar.init()` has NO currency
 * parameter (confirmed against the CMS's actual real `init()` call, which
 * never passes one) — the vendor widget always renders its own prices in
 * GBP, and the CMS converts the rendered TEXT after the fact rather than
 * through the widget. We do the same here via `useCurrency()`.
 *
 * `convertFromGbp` is expected to ALSO apply the commission/tax markup
 * before currency conversion — CMS's own equivalent (`applyMarkup()` in
 * `theatre-detail-page.js`) runs the vendor's rendered face price through
 * its `commissionPricingMultiplier` THEN the exchange rate
 * (`createPricingEngine`'s `calculatePrice`), never the raw net price. The
 * caller composes that here via `applyPricingCoefficients` + `convertPrice`
 * — see this component's `pricingCoefficients` state/ref.
 *
 * The GBP amount is cached on each element's `data-gbp-amount` the FIRST
 * time it's seen, and every rewrite (including subsequent currency
 * switches) re-converts from that cached original — never from whatever
 * is currently displayed, which would compound/corrupt the amount on a
 * second currency switch (e.g. GBP→EUR→GBP would otherwise not round-trip
 * back to the real original).
 */
function rewritePrices(root: HTMLElement, convertFromGbp: (amount: number) => number, symbol: string) {
  const priceEls = root.querySelectorAll<HTMLElement>(
    '[class*="performance-item__price"], [class*="mobile-footer__price-value"]',
  );
  priceEls.forEach((el) => {
    let original = el.dataset.gbpAmount;
    if (original === undefined) {
      // Decimal part is OPTIONAL — the live sandbox affiliate renders whole-
      // pound amounts with no ".00" suffix at all (e.g. "From £73"), which
      // the earlier `\.\d{2}`-required pattern silently failed to match,
      // leaving `data-gbp-amount` unset and the price stuck in GBP forever
      // (confirmed live: no console error, the element was just skipped).
      const m = /([\d,]+(?:\.\d{1,2})?)/.exec(el.textContent || '');
      if (!m) return;
      original = m[1].replace(/,/g, '');
      el.dataset.gbpAmount = original;
    }
    const amount = parseFloat(original);
    if (Number.isNaN(amount)) return;
    const converted = convertFromGbp(amount).toFixed(2);
    // Only the "performance item" price carries the "From " prefix — the
    // mobile footer's own price-value element renders the bare amount.
    const hasFromPrefix = /^From\s/i.test(el.textContent || '');
    const nextText = hasFromPrefix ? `From ${symbol}${converted}` : `${symbol}${converted}`;
    // Guard against a write when nothing actually changed: `el.textContent
    // = x` replaces the element's text-node child even when `x` equals the
    // current value, which is itself a `childList` mutation — and this
    // function runs FROM a `childList`-observing MutationObserver
    // callback. An unconditional write here re-triggers that same
    // observer forever (confirmed live — froze the page/hung the render
    // loop). Only write when the text is actually different.
    if (el.textContent !== nextText) el.textContent = nextText;
  });
}

/**
 * Highlights the ALREADY-selected performance's own "Select" link
 * (`rgb(0, 151, 1)`, exact color per explicit request) and hides every
 * OTHER performance-item row for the same date — used on the Seat
 * Selection page so its sidebar calendar shows only the one performance
 * the user already picked on the Show Detail Page, not a re-pickable
 * list (that page's own "Change" link is what lets them pick a different
 * one, at which point the widget is browsing a different date and this
 * function is a no-op again — see below).
 *
 * Matches on the link's OWN href — the vendor's confirmed redirect shape
 * is `/booking/{eventUrlId}/{MM-YYYY}/{performanceId}` (see this file's
 * other doc comments) — since the rendered DOM exposes no other
 * performanceId attribute. `.custom-button-link` is the vendor's own
 * documented customization hook for these links (already relied on
 * elsewhere in this file/shows.css).
 *
 * Deliberately a no-op (leaves every row untouched, restoring visibility
 * on any previously-hidden ones) when NONE of the currently-rendered rows
 * match `selectedPerformanceId` — that means the widget is showing a
 * DIFFERENT date's list right now (e.g. mid-browsing after "Change"), and
 * hiding everything then would look broken rather than helpful.
 *
 * Also reports the matched row's own rendered time text (e.g. `"7:30 pm"`,
 * from its sibling `[class*="performance-item__time"]`) via `onTime` — the
 * ONLY place that exact time is available: our own `initialDate` prop is
 * day-precision only (see `ltd-api.ts`), and the mock build's own
 * performance list lives in a completely different id space (see
 * `SeatSelectionClient.tsx`'s `selectedPerformance` lookup), so reading it
 * straight from the vendor's own DOM is more reliable than deriving it.
 *
 * Also does two things to every rendered link regardless of match, per explicit request:
 * (1) strips `target` — the vendor renders these with `target="_blank"` by default (confirmed
 * necessary: the reference CMS's own `bindSelectButtons()` explicitly does
 * `link.removeAttribute('target')` too), which would otherwise open the seat-selection page in a
 * new tab instead of navigating there in the same one; (2) binds a one-time `click` listener
 * (`dataset.navLoadingBound` flag, same caching pattern as `originalLabel` above — this whole
 * function re-runs on every widget DOM mutation, so a plain `addEventListener` here without a
 * guard would stack a new listener on every re-render) that fires `onNavigate` — this is a REAL
 * full-page navigation (the vendor's own redirect to `/booking/…`), not a fetch we control, so
 * nothing else signals it's in flight; `onNavigate` shows a loading overlay for the moment before
 * the browser actually unloads this page. The listener skips `onNavigate` on a modifier-key or
 * non-primary-button click (Ctrl/Cmd/Shift/Alt/middle-click, i.e. "open in new tab") — those never
 * unload THIS tab (the new tab does), so setting `navigating` here would strand the current tab
 * behind the overlay forever.
 */
function getActiveCalendarDate(root: HTMLElement): string | null {
  const activeCell = root.querySelector<HTMLElement>(
    'td.active, td[class*="selected"], td[class*="active"], [class*="calendar__day--selected"], [class*="calendar-day--selected"], [class*="day-cell--selected"]',
  );
  if (activeCell) {
    // Same priority as `handleRootClick` below — derive from the visible day number + selected
    // month/year first, `data-date` only as a fallback. See that handler's doc comment for why.
    const dayText = activeCell.textContent?.trim();
    const dayNum = parseInt(dayText || '', 10);
    const select = root.querySelector('select');
    if (dayNum >= 1 && dayNum <= 31 && select) {
      const selectedOption = select.options[select.selectedIndex]?.textContent?.trim() || '';
      const m = /^(\w+)\s+(\d{4})$/.exec(selectedOption);
      if (m) {
        const monthIdx = MONTH_NAMES.indexOf(m[1]);
        const year = parseInt(m[2], 10);
        if (monthIdx >= 0 && year > 2000) {
          const mm = String(monthIdx + 1).padStart(2, '0');
          const dd = String(dayNum).padStart(2, '0');
          return `${year}-${mm}-${dd}`;
        }
      }
    }
    const dataDate = activeCell.getAttribute('data-date');
    if (dataDate && /^\d{4}-\d{2}-\d{2}$/.test(dataDate)) return dataDate;
  }
  return null;
}

/**
 * Substitutes OUR internal show id for LTD's own `eventUrlId` slug segment in a vendor-rendered
 * "Select" link, before the browser follows it — mirrors the legacy CMS's `theatre-detail-page.js`
 * `bindSelectButtons()`, which swaps in its own known `event_id` before navigating rather than
 * letting LTD's slug reach the booking page at all. This is why the Seat Selection Page's own
 * `resolveShow()` no longer needs to GUESS `eventUrlId` back to an internal id via
 * `slugifyForLtd()` (a real 404 source when a show's name doesn't round-trip through that guess) —
 * the URL it receives already carries the real id directly. Only rewrites when the exact
 * `eventUrlId` segment is found (so an unrecognized href shape is left untouched rather than
 * corrupted).
 */
function substituteEventUrlId(
  href: string,
  eventUrlId: string | null | undefined,
  internalShowId: string | null | undefined,
): string {
  if (!eventUrlId || !internalShowId) return href;
  const [basePath, search] = href.split('?');
  const segments = basePath.split('/');
  const idx = segments.indexOf(eventUrlId);
  if (idx === -1) return href;
  segments[idx] = internalShowId;
  const newBase = segments.join('/');
  return search !== undefined ? `${newBase}?${search}` : newBase;
}

function disableSoldOutButtons(root: HTMLElement) {
  const elements = root.querySelectorAll<HTMLElement>(
    'a.custom-button-link, button.custom-button-link, [class*="_custom-button_"], [class*="performance-item__btn"], [class*="performance-item__button"], [class*="performance-item"] a, [class*="performance-item"] button, [class*="calendar"] button, [class*="calendar"] a'
  );
  elements.forEach((el) => {
    const text = (el.textContent || '').trim().toLowerCase();
    if (text === 'sold out' || text.includes('sold out')) {
      if (el.tagName === 'BUTTON') {
        (el as HTMLButtonElement).disabled = true;
      }
      el.setAttribute('disabled', 'true');
      el.setAttribute('aria-disabled', 'true');
      el.classList.add('disabled');
      el.style.setProperty('pointer-events', 'none', 'important');
      el.style.setProperty('cursor', 'not-allowed', 'important');
      el.style.setProperty('opacity', '0.5', 'important');
      if (el.tagName === 'A') {
        el.removeAttribute('href');
      }
    }
  });
}

function markSelectedPerformance(
  root: HTMLElement,
  selectedPerformanceId: string | null,
  onTime?: (time: string | null) => void,
  onNavigate?: () => void,
  ticketQuantity?: number,
  selectedDate?: string | null,
  eventUrlId?: string | null,
  internalShowId?: string | null,
) {
  const links = root.querySelectorAll<HTMLAnchorElement>('a.custom-button-link');
  if (links.length === 0) {
    disableSoldOutButtons(root);
    return;
  }

  const effDate = selectedDate || getActiveCalendarDate(root);

  let matched: HTMLAnchorElement | null = null;
  if (selectedPerformanceId) {
    links.forEach((link) => {
      const href = link.getAttribute('href') || '';
      const segments = href.split('?')[0].split('/').filter(Boolean);
      if (segments[segments.length - 1] === selectedPerformanceId) matched = link;
    });
  }

  links.forEach((link) => {
    link.removeAttribute('target');

    // Cache the vendor's own original label the first time this link is seen — moved ahead of
    // the href/click-listener wiring below (previously only read much later in this function,
    // after we'd already have started touching the link) so the "Sold Out" check right after can
    // read the REAL vendor label.
    if (link.dataset.originalLabel === undefined) link.dataset.originalLabel = link.textContent || 'Select';

    // The vendor renders a sold-out performance-time button as an ordinary, fully-clickable link
    // — same gap as the calendar day cells `disableSoldOutCells` handles below, just for the
    // per-time "Select" buttons instead of day cells. Block it here and skip all the href
    // rewriting / click-to-navigate wiring below entirely, so a sold-out time can never be
    // selected.
    if ((link.dataset.originalLabel || '').trim().toLowerCase() === 'sold out') {
      link.style.setProperty('pointer-events', 'none', 'important');
      link.style.setProperty('cursor', 'not-allowed', 'important');
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');
      link.classList.add('shows-calendar-cell--sold-out');
      return;
    }

    if (ticketQuantity && ticketQuantity > 0) {
      link.dataset.ticketQuantity = String(ticketQuantity);
    }
    if (effDate) {
      link.dataset.selectedDate = effDate;
    }
    if (eventUrlId) {
      link.dataset.eventUrlId = eventUrlId;
    }
    if (internalShowId) {
      link.dataset.internalShowId = internalShowId;
    }

    // Ensure quantity/date params are updated AND our internal id replaces LTD's eventUrlId
    const rawHref = link.getAttribute('href') || '';
    if (rawHref) {
      const substituted = substituteEventUrlId(rawHref, eventUrlId, internalShowId);
      const [basePath, search = ''] = substituted.split('?');
      const params = new URLSearchParams(search);
      if (ticketQuantity && ticketQuantity > 0) {
        params.set('quantity', String(ticketQuantity));
      }
      if (effDate) {
        params.set('date', effDate);
      }
      const newHref = `${basePath}?${params.toString()}`;
      if (rawHref !== newHref) {
        link.setAttribute('href', newHref);
      }
    }

    const isSoldOut = (link.textContent || '').trim().toLowerCase().includes('sold out');
    if (isSoldOut) {
      if (link.tagName === 'BUTTON') {
        (link as unknown as HTMLButtonElement).disabled = true;
      }
      link.setAttribute('disabled', 'true');
      link.setAttribute('aria-disabled', 'true');
      link.classList.add('disabled');
      link.style.setProperty('pointer-events', 'none', 'important');
      link.style.setProperty('cursor', 'not-allowed', 'important');
      link.style.setProperty('opacity', '0.5', 'important');
      link.removeAttribute('href');
    }

    if (link.dataset.navLoadingBound === undefined) {
      link.dataset.navLoadingBound = 'true';
      link.addEventListener('click', (e) => {
        // If this performance is already selected (the green "Selected" button), do not trigger navigation/action
        if (
          link.dataset.isSelectedPerformance === 'true' ||
          link.classList.contains('shows-performance-selected') ||
          (link.textContent || '').trim().toLowerCase() === 'selected'
        ) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        const mouseEvent = e as MouseEvent;
        const opensInNewTab =
          mouseEvent.button !== 0 ||
          mouseEvent.ctrlKey ||
          mouseEvent.metaKey ||
          mouseEvent.shiftKey ||
          mouseEvent.altKey;
        if (opensInNewTab) return;

        const currentHref = link.getAttribute('href') || '';
        if (currentHref) {
          const currentEventUrlId = link.dataset.eventUrlId || eventUrlId;
          const currentInternalShowId = link.dataset.internalShowId || internalShowId;
          const currentQty = link.dataset.ticketQuantity || (ticketQuantity ? String(ticketQuantity) : '1');
          const currentEffDate = link.dataset.selectedDate || selectedDate || getActiveCalendarDate(root);

          const substituted = substituteEventUrlId(currentHref, currentEventUrlId, currentInternalShowId);
          const [basePath, search = ''] = substituted.split('?');
          const params = new URLSearchParams(search);
          if (currentQty) {
            params.set('quantity', currentQty);
          }
          if (currentEffDate) {
            params.set('date', currentEffDate);
          }
          const newHref = `${basePath}?${params.toString()}`;
          link.setAttribute('href', newHref);
        }

        onNavigate?.();
      });
    }

    const wrapper = link.closest<HTMLElement>('[class*="performance-item__wrapper"]') || link;

    if (matched && link === matched) {
      link.dataset.isSelectedPerformance = 'true';
      link.classList.add('shows-performance-selected');
      link.style.setProperty('background-color', 'rgb(0, 151, 1)', 'important');
      wrapper.style.removeProperty('display');
      if (link.textContent !== 'Selected') link.textContent = 'Selected';
      const timeEl = wrapper.querySelector<HTMLElement>('[class*="performance-item__time"]');
      onTime?.(timeEl?.textContent?.trim() || null);
    } else if (matched) {
      link.dataset.isSelectedPerformance = 'false';
      link.classList.remove('shows-performance-selected');
      wrapper.style.setProperty('display', 'none', 'important');
    } else {
      // No match on the currently-rendered date — restore anything a
      // PREVIOUS date's render may have hidden/colored/relabeled.
      link.dataset.isSelectedPerformance = 'false';
      link.classList.remove('shows-performance-selected');
      link.style.removeProperty('background-color');
      wrapper.style.removeProperty('display');
      if (link.textContent !== link.dataset.originalLabel) link.textContent = link.dataset.originalLabel;
    }
  });

  disableSoldOutButtons(root);

  if (!matched) onTime?.(null);
}

/** True when a calendar day cell's own rendered label is "Sold Out" — the vendor widget still
 *  renders these cells as ordinary, fully-clickable table cells (no `disabled` attribute or
 *  `pointer-events` of its own), so a sold-out date was selectable exactly like any other date
 *  before `disableSoldOutCells` below started running. */
function isSoldOutCell(cell: Element): boolean {
  return (cell.textContent || '').trim().toLowerCase().includes('sold out');
}

/** Same cell selector `handleRootClick` below uses to find the clicked day. Re-run on every
 *  MutationObserver tick (month navigation re-renders the whole table) so newly-rendered months'
 *  sold-out cells get the same treatment, not just the ones visible at mount time. */
function disableSoldOutCells(root: HTMLElement) {
  const cells = root.querySelectorAll<HTMLElement>(
    'td, [data-date], [class*="calendar__day"], [class*="calendar-day"], [class*="day-cell"]',
  );
  cells.forEach((cell) => {
    if (!isSoldOutCell(cell)) {
      // Restore a cell that used to be sold out but no longer is (e.g. the vendor's own data
      // refreshed) — avoids permanently locking a date that later becomes available again.
      if (cell.dataset.soldOutDisabled) {
        cell.style.removeProperty('pointer-events');
        cell.style.removeProperty('cursor');
        cell.removeAttribute('aria-disabled');
        cell.classList.remove('shows-calendar-cell--sold-out');
        delete cell.dataset.soldOutDisabled;
      }
      return;
    }
    if (cell.dataset.soldOutDisabled) return; // already handled this render pass
    cell.dataset.soldOutDisabled = 'true';
    cell.style.setProperty('pointer-events', 'none', 'important');
    cell.style.setProperty('cursor', 'not-allowed', 'important');
    cell.setAttribute('aria-disabled', 'true');
    cell.classList.add('shows-calendar-cell--sold-out');
    // Belt-and-braces: strip keyboard focusability too, in case the vendor's own markup makes
    // the cell (or something inside it) tab-reachable — `pointer-events: none` alone only blocks
    // mouse/touch, not Enter/Space activation via keyboard.
    cell.querySelectorAll<HTMLElement>('a, button, [tabindex]').forEach((el) => {
      el.setAttribute('tabindex', '-1');
      el.setAttribute('aria-disabled', 'true');
    });
  });
}

/**
 * Derives the ISO date (YYYY-MM-DD) from a calendar day cell by reading the cell's rendered day
 * number combined with the month switcher dropdown's currently selected option.
 */
function deriveDateFromCell(cell: Element, root: HTMLElement): string | null {
  if (isSoldOutCell(cell as HTMLElement)) return null;

  const dayDiv = cell.querySelector('div') || cell;
  const dayText = dayDiv.textContent?.trim() || '';
  const dayMatch = /^\d{1,2}/.exec(dayText);
  const dayNum = dayMatch ? parseInt(dayMatch[0], 10) : parseInt(dayText, 10);

  const select = root.querySelector('select');
  if (dayNum >= 1 && dayNum <= 31 && select && select.selectedIndex >= 0) {
    const selectedOption = select.options[select.selectedIndex]?.textContent?.trim() || '';
    const m = /^([A-Za-z]+)\s+(\d{4})$/.exec(selectedOption);
    if (m) {
      const monthIdx = MONTH_NAMES.indexOf(m[1]);
      const year = parseInt(m[2], 10);
      if (monthIdx >= 0 && year > 2000) {
        const mm = String(monthIdx + 1).padStart(2, '0');
        const dd = String(dayNum).padStart(2, '0');
        return `${year}-${mm}-${dd}`;
      }
    }
  }

  const dataDate = cell.getAttribute('data-date');
  if (dataDate && /^\d{4}-\d{2}-\d{2}$/.test(dataDate)) {
    return dataDate;
  }

  return null;
}

/**
 * Reads the date currently selected in the LTD calendar widget DOM.
 */
function getSelectedDateFromCalendar(root: HTMLElement): string | null {
  const selectedCell = root.querySelector(
    '[class*="widget-calendar-cell--selected"], [class*="cell--selected"], [class*="calendar__day--selected"], [class*="day-cell--selected"], [aria-selected="true"]'
  );
  if (!selectedCell) return null;
  return deriveDateFromCell(selectedCell, root);
}

export interface LtdCalendarWidgetProps {
  /** LTD's own event identifier, e.g. `'the-lion-king-tickets'` — the
   *  required `eventUrlId` param for `LTD.Calendar.init`. Falsy renders the
   *  empty state instead of loading the vendor script at all. */
  eventUrlId: string | null | undefined;
  /** Our own internal `Show` doc id (`ShowDetailRecord.showId`, a real Mongo `_id`) — when
   *  provided, every rendered "Select" link has its `eventUrlId` path segment swapped for this id
   *  before it can be clicked/copied/opened in a new tab, so the Seat Selection Page never has to
   *  reverse-guess LTD's slug back to a show (see `substituteEventUrlId`'s doc comment for why).
   *  Omit only for a caller that has no internal id to substitute (there shouldn't be one — every
   *  real show detail/seat-selection render has already resolved its own `Show` doc). */
  internalShowId?: string | null;
  /** DOM id the widget mounts into. Defaults to `'ltd-calendar-widget'`;
   *  override if this component is ever rendered more than once on the
   *  same page (LTD mounts by a single global element id, so two
   *  instances need two distinct ids). */
  rootElementId?: string;
  /** Per the docs, non-`'default'` themes are "available in a future
   *  release" — omit this until LTD confirms support; passed through
   *  as-is rather than assumed. */
  theme?: 'default' | 'light' | 'dark';
  /** Upper bound for the ticket-quantity stepper below. The legacy CMS
   *  (`theatre-detail-page.js` `updateRoomSelectionLimit`) recomputes this
   *  per SELECTED calendar date from that date's own performances; we
   *  don't have a hook into the vendor widget's internal date-selection
   *  state, so this is a simpler, still real-data-driven bound (the max
   *  `availableTickets` across ALL of the show's performances) passed down
   *  by the caller. Defaults to 9 (common "up to 9 tickets" convention)
   *  when the caller has no performance data at all. */
  maxTicket?: number;
  /** ISO date string — the month/date the widget should open on, instead
   *  of defaulting to today. Confirmed as a real (if undocumented-on-the-
   *  fetched-docs-page) `LTD.Calendar.init()` param — the legacy CMS's own
   *  production `init()` call passes `defaultDate: new Date(bookingDate)
   *  .toISOString()` (`theatre-detail-page.js`). Used on the Seat Selection
   *  Page so it opens on whatever date was picked on the Show Detail Page,
   *  instead of always resetting to today. */
  defaultDate?: string;
  /** Set `false` to suppress the internal "Tickets [count]" header —
   *  for callers (the Seat Selection Page) that render their own quantity
   *  control actually wired to something (unlike this one, which is purely
   *  visual — see `TicketQuantityHeader`'s doc comment) and would
   *  otherwise show two "Tickets" headers stacked. Defaults to `true`. */
  showTicketHeader?: boolean;
  /** Set while the mobile calendar sheet is open. On a narrow viewport the
   *  vendor renders NO inline calendar at all — `LTD.Calendar.toggle()` mounts
   *  its own `position: fixed; z-index: 9999` overlay instead (confirmed live;
   *  `.widget-calendar-main-wrapper--overlay`). The inline "Tickets − n +"
   *  stepper below therefore has nothing to attach to, which is the reported
   *  "ticket control missing when the calendar opens on mobile". When this is
   *  set, the stepper is PORTALLED into that overlay instead — the same place
   *  the reference CMS's `injectTicketCounter()` puts its own copy (see
   *  `theaters-cms-reference-code/client-js/theatre-detail-page.js`, which
   *  prepends into `mobileOverlay.firstElementChild`). */
  forceTicketHeader?: boolean;
  /** When set, the matching performance's own "Select" link is
   *  highlighted green (`rgb(0, 151, 1)`) and every OTHER performance for
   *  the same date is hidden — see `markSelectedPerformance`'s doc
   *  comment. Omit on the Show Detail Page (nothing is "already selected"
   *  there); the Seat Selection Page's `DateSelectionPanel` passes the
   *  performance id the user arrived with. */
  selectedPerformanceId?: string | number | null;
  /** Fired with the selected performance's own rendered time (e.g.
   *  `"7:30 pm"`), or `null` while the widget is showing a date that
   *  doesn't contain it — see `markSelectedPerformance`'s doc comment for
   *  why this is the only reliable source for the exact time. */
  onSelectedPerformanceTime?: (time: string | null) => void;
  /** Fired whenever the user clicks/selects a date on the calendar, passing the ISO date (YYYY-MM-DD) */
  onDateSelect?: (dateIso: string) => void;
  /** Initial ticket quantity for the stepper. Defaults to 1. */
  initialQuantity?: number;
  /** Fired whenever ticket quantity changes. */
  onQuantityChange?: (quantity: number) => void;
}

/**
 * "Tickets" heading + [−] [count] [+] quantity stepper — visual port of
 * the CMS's `injectTicketCounter()` (see the module doc comment).
 */
function TicketQuantityHeader({
  count,
  maxTicket,
  onChange,
  variant,
}: {
  count: number;
  maxTicket: number;
  onChange: (next: number) => void;
  /** `'overlay'` when rendered inside the vendor's mobile overlay, where it
   *  inherits none of the booking panel's padding. */
  variant?: 'overlay';
}) {
  return (
    <div
      className={`shows-detail-ltd-ticket-header${
        variant === 'overlay' ? ' shows-detail-ltd-ticket-header--overlay' : ''
      }`}
    >
      <h4 className="shows-detail-ltd-ticket-header-title">Tickets</h4>
      <div className="shows-detail-ltd-ticket-stepper" role="group" aria-label="Ticket quantity">
        <button
          type="button"
          onClick={() => onChange(Math.max(1, count - 1))}
          disabled={count <= 1}
          aria-label="Decrease ticket quantity"
        >
          <i className="fa fa-minus"></i>
        </button>
        <span className="shows-detail-ltd-ticket-count" aria-live="polite">
          {count}
        </span>
        <button
          type="button"
          onClick={() => onChange(Math.min(maxTicket, count + 1))}
          disabled={count >= maxTicket}
          aria-label="Increase ticket quantity"
        >
          <i className="fa fa-plus"></i>
        </button>
      </div>
    </div>
  );
}

/**
 * Skeleton placeholder shown while the vendor script loads and the widget
 * renders its own DOM — keeps the booking panel's height stable instead of
 * collapsing to nothing and then jumping once the real widget appears.
 */
function CalendarSkeleton() {
  return (
    <div className="shows-detail-ltd-calendar-skeleton" aria-hidden="true">
      <div className="shows-detail-ltd-calendar-skeleton-row shows-detail-ltd-calendar-skeleton-row--header" />
      <div className="shows-detail-ltd-calendar-skeleton-grid">
        {Array.from({ length: 35 }, (_, i) => (
          <div key={i} className="shows-detail-ltd-calendar-skeleton-cell" />
        ))}
      </div>
    </div>
  );
}

export function LtdCalendarWidget({
  eventUrlId,
  internalShowId = null,
  rootElementId = 'ltd-calendar-widget',
  theme,
  maxTicket = 9,
  defaultDate,
  showTicketHeader = true,
  forceTicketHeader = false,
  selectedPerformanceId = null,
  onSelectedPerformanceTime,
  onDateSelect,
  initialQuantity = 1,
  onQuantityChange,
}: LtdCalendarWidgetProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const tableObserverRef = useRef<MutationObserver | null>(null);
  // `${rootElementId}::${eventUrlId}` of the last target actually passed to
  // `LTD.Calendar.init()` — see `initWidget`'s doc comment for why this guard exists.
  const initedForKeyRef = useRef<string | null>(null);
  const [state, setState] = useState<WidgetState>(eventUrlId ? 'loading' : 'empty');

  const [ticketQuantity, setTicketQuantity] = useState(initialQuantity);
  const ticketQuantityRef = useRef(ticketQuantity);
  ticketQuantityRef.current = ticketQuantity;

  const [selectedDate, setSelectedDate] = useState<string | null>(defaultDate ? defaultDate.split('T')[0] : null);
  const selectedDateRef = useRef(selectedDate);
  selectedDateRef.current = selectedDate;

  useEffect(() => {
    if (defaultDate) {
      const d = defaultDate.split('T')[0];
      setSelectedDate(d);
      selectedDateRef.current = d;
    }
  }, [defaultDate]);

  useEffect(() => {
    setTicketQuantity(initialQuantity);
  }, [initialQuantity]);

  function handleTicketQuantityChange(next: number) {
    setTicketQuantity(next);
    ticketQuantityRef.current = next;
    onQuantityChange?.(next);
    const root = rootRef.current;
    if (root) {
      markSelectedPerformance(
        root,
        selectedPerformanceIdRef.current == null ? null : String(selectedPerformanceIdRef.current),
        (time) => onSelectedPerformanceTimeRef.current?.(time),
        () => setNavigating(true),
        next,
        selectedDateRef.current,
        eventUrlId,
        internalShowIdRef.current,
      );
    }
  }

  const onDateSelectRef = useRef(onDateSelect);
  onDateSelectRef.current = onDateSelect;

  const { convertPrice, currencySymbol } = useCurrency();
  // Kept fresh on every render (no effect dependency) so the persistent
  // MutationObserver callback below — created once per `eventUrlId` — never
  // reads a stale currency from whatever render it happened to be created
  // during. `convertPrice`/`currencySymbol` themselves aren't in that
  // effect's dependency array on purpose: re-subscribing the observer on
  // every currency change would mean briefly missing mutations mid-swap.
  const currencyRef = useRef({ convertPrice, currencySymbol });
  currencyRef.current = { convertPrice, currencySymbol };

  // Commission/tax markup for the vendor's own rendered GBP face prices — same
  // `GET /performances/pricing-coefficients` endpoint + `applyPricingCoefficients`
  // helper `LtdSeatPlanWidget`/`SeatSelectionClient` already use for this exact
  // "vendor-rendered face price never passed through `calculatePrice`" scenario.
  // Fetched once (independent of `eventUrlId` — the coefficients are env-derived
  // commission rates, not show-specific). `null` while loading is treated as
  // identity (no markup yet) by `applyPricingCoefficients`'s callers below, same
  // graceful-degrade fallback as the seat-plan widget on fetch failure.
  const [pricingCoefficients, setPricingCoefficients] = useState<PricingCoefficients | null>(null);
  const pricingCoefficientsRef = useRef(pricingCoefficients);
  pricingCoefficientsRef.current = pricingCoefficients;

  useEffect(() => {
    let cancelled = false;
    showsClient
      .getPricingCoefficients()
      .then((coefficients) => {
        if (!cancelled) setPricingCoefficients(coefficients);
      })
      .catch(() => {
        if (!cancelled) setPricingCoefficients({ a: 1, b: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Same "kept fresh via ref, not an effect dep" reasoning as currencyRef.
  const selectedPerformanceIdRef = useRef(selectedPerformanceId);
  selectedPerformanceIdRef.current = selectedPerformanceId;
  const internalShowIdRef = useRef(internalShowId);
  internalShowIdRef.current = internalShowId;
  const onSelectedPerformanceTimeRef = useRef(onSelectedPerformanceTime);
  onSelectedPerformanceTimeRef.current = onSelectedPerformanceTime;
  // Shown while a "Select" link's real full-page navigation is in flight — see
  // `markSelectedPerformance`'s doc comment on `onNavigate`. `setNavigating` has a stable
  // identity across renders (a `useState` setter), so it's safe to reference directly inside the
  // persistent MutationObserver callback below without a ref.
  const [navigating, setNavigating] = useState(false);
  // Tracks whether the widget currently has an actual calendar TABLE
  // rendered inline — true on desktop once ready, but stays false on
  // narrow viewports where `isStickyMobileFooter` collapses the inline
  // widget down to just its own sticky footer button (no inline table
  // until the user taps it, at which point LTD renders its own overlay).
  // Gates the "Tickets" quantity header below — showing it above blank
  // space when only the footer is present would look broken. Known gap:
  // this does not (yet) re-show the header inside that mobile overlay,
  // since it's the vendor's own overlay DOM, not necessarily reachable as
  // a descendant of `rootRef` — same class of limitation as the "quantity
  // doesn't feed into Select" gap noted on `TicketQuantityHeader`.
  const [hasTable, setHasTable] = useState(false);

  function stopWatching() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }

  /** Flips to the "ready" state the first time the vendor script injects
   *  anything into the root element, and to "error" if nothing has
   *  appeared after `RENDER_TIMEOUT_MS` — see the module doc comment for
   *  why this is a heuristic rather than a documented event. */
  function watchForRender() {
    const root = rootRef.current;
    if (!root) return;
    stopWatching();
    observerRef.current = new MutationObserver(() => {
      if (root.childElementCount > 0) {
        setState('ready');
        stopWatching();
      }
    });
    observerRef.current.observe(root, { childList: true });
    timeoutRef.current = setTimeout(() => {
      if (root.childElementCount === 0) setState('error');
      stopWatching();
    }, RENDER_TIMEOUT_MS);
  }

  function initWidget() {
    if (!eventUrlId) return;
    const w = window as unknown as LtdCalendarGlobal;
    if (!w.LTD?.Calendar) {
      // Called from `onLoad`/`onReady` but the global never materialized —
      // the CDN likely served something unexpected. Genuine failure, not
      // guessed data.
      setState('error');
      return;
    }

    // `next/script`'s `onLoad` and `onReady` both fire from the SAME `load`
    // event on the widget's first-ever load (`afterLoad()` calls `onReady()`
    // right after `onLoad`), and on every remount `onReady` fires again
    // (Script's own "already loaded" effect) at the same time as this
    // component's own `if (w.LTD?.Calendar) initWidget()` fallback below —
    // so this function gets called twice per load/mount with no reliable way
    // to remove just one of those triggers. `LTD.Calendar.init()` has no
    // `destroy()`, so two un-deduped calls raced to render two independent
    // widget instances into the same root, each with its own idea of "which
    // date is selected" — the reported "multiple dates selected" bug, and
    // (via the resulting duplicate/overlapping renders) the "preloaded but
    // not used"-adjacent double-render churn. Guard by target so only the
    // FIRST call per (rootElementId, eventUrlId) does anything; `handleRetry`
    // explicitly clears this to force a real re-init.
    const initKey = `${rootElementId}::${eventUrlId}`;
    if (initedForKeyRef.current === initKey) return;
    initedForKeyRef.current = initKey;

    const affiliateId = process.env.NEXT_PUBLIC_SHOWS_LTD_AFFILIATE_ID || LTD_SANDBOX_AFFILIATE_ID;
    if (process.env.NODE_ENV !== 'production' && !process.env.NEXT_PUBLIC_SHOWS_LTD_AFFILIATE_ID) {
      // Should not normally fire — see the LTD_SANDBOX_AFFILIATE_ID doc
      // comment for where the real value is supposed to come from.
      // eslint-disable-next-line no-console
      console.info(
        '[LTD.Calendar] NEXT_PUBLIC_SHOWS_LTD_AFFILIATE_ID is not set (check apps/web/.env.local) — ' +
        'falling back to the public sandbox affiliateId. Every show will render the same canned demo ' +
        'dataset until the real affiliate ID env var is restored.',
      );
    }

    // `LTD.Calendar.init` documents no "re-init with a new eventUrlId"
    // behavior, so clear any previous render before asking the widget to
    // mount again into the same root element.
    if (rootRef.current) rootRef.current.innerHTML = '';
    watchForRender();

    try {
      w.LTD.Calendar.init({
        rootElementId,
        affiliateId,
        eventUrlId,
        performanceSelectionRedirectUrl: `${window.location.origin}${SHOWS_BASE_PATH}`,
        ...(theme ? { theme } : {}),
        ...(defaultDate ? { defaultDate } : {}),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[LTD.Calendar] init() threw', err);
      setState('error');
      stopWatching();
    }
  }

  useEffect(() => {
    setState(eventUrlId ? 'loading' : 'empty');
    setHasTable(false);
    if (!eventUrlId) return;

    // Persistent (never disconnected until unmount/eventUrlId change) —
    // unlike `watchForRender`'s one-shot ready check, whether an inline
    // table is present can change over the widget's lifetime (e.g. a
    // narrow-viewport reload where it never appears at all). Also drives
    // the month-dropdown pruning and price-currency rewrite, since both
    // need to re-apply every time the widget re-renders its own content
    // (e.g. navigating months) — not just once at initial render.
    //
    // MUST be attached BEFORE the `initWidget()` call below: when
    // `window.LTD.Calendar` is already loaded (client-side nav to another
    // show, or React Strict Mode's dev-only double effect invocation),
    // `initWidget()` synchronously clears and re-injects the widget's DOM.
    // If the observer were attached after that call, it would miss this
    // very first injection — and since nothing else mutates the DOM until
    // the user changes month/date, the initial prices would be stuck in
    // GBP forever (confirmed live: `data-gbp-amount` never got set on the
    // first performance-price element when the script was already cached).
    const root = rootRef.current;
    if (root) {
      tableObserverRef.current = new MutationObserver(() => {
        setHasTable(!!root.querySelector('table'));
        pruneMonthOptions(root);
        disableSoldOutCells(root);
        rewritePrices(
          root,
          (amount) =>
            currencyRef.current.convertPrice(
              pricingCoefficientsRef.current ? applyPricingCoefficients(pricingCoefficientsRef.current, amount) : amount,
              'GBP',
            ),
          currencyRef.current.currencySymbol,
        );

        // Detect the date currently selected in the LTD calendar widget DOM (initial render / month change)
        const currentSelectedDate = getSelectedDateFromCalendar(root);
        if (currentSelectedDate && currentSelectedDate !== selectedDateRef.current) {
          setSelectedDate(currentSelectedDate);
          selectedDateRef.current = currentSelectedDate;
          onDateSelectRef.current?.(currentSelectedDate);
        }

        markSelectedPerformance(
          root,
          selectedPerformanceIdRef.current == null ? null : String(selectedPerformanceIdRef.current),
          (time) => onSelectedPerformanceTimeRef.current?.(time),
          () => setNavigating(true),
          ticketQuantityRef.current,
          selectedDateRef.current,
          eventUrlId,
          internalShowIdRef.current,
        );
      });
      tableObserverRef.current.observe(root, { childList: true, subtree: true });
      // Delegated click listener to detect user date selection on the calendar
      const handleRootClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;

        const cell = target.closest('td, [data-date], [class*="calendar__day"], [class*="calendar-day"], [class*="day-cell"], [class*="widget-calendar-cell"]');
        if (cell) {
          // Belt-and-braces alongside `disableSoldOutCells`' `pointer-events: none` — bail out
          // here too so a sold-out date can never be selected even if something (a keyboard
          // activation, a re-render that hasn't been processed by the observer yet) slips past
          // the CSS block.
          if (isSoldOutCell(cell)) return;
          const clickedIso = deriveDateFromCell(cell, root);

          if (clickedIso) {
            setSelectedDate(clickedIso);
            selectedDateRef.current = clickedIso;
            onDateSelectRef.current?.(clickedIso);
            markSelectedPerformance(
              root,
              selectedPerformanceIdRef.current == null ? null : String(selectedPerformanceIdRef.current),
              (time) => onSelectedPerformanceTimeRef.current?.(time),
              () => setNavigating(true),
              ticketQuantityRef.current,
              clickedIso,
              eventUrlId,
              internalShowIdRef.current,
            );
          }
        }
      };

      root.addEventListener('click', handleRootClick);
      (root as unknown as { _handleRootClick?: typeof handleRootClick })._handleRootClick = handleRootClick;
    }

    // Next's <Script> only fires onLoad/onReady once globally per `src` —
    // a second mount (e.g. client-side navigation to another show) needs
    // its own explicit init if the script already finished loading.
    const w = window as unknown as LtdCalendarGlobal;
    if (w.LTD?.Calendar) initWidget();

    return () => {
      stopWatching();
      tableObserverRef.current?.disconnect();
      tableObserverRef.current = null;
      if (root && (root as unknown as { _handleRootClick?: (e: MouseEvent) => void })._handleRootClick) {
        root.removeEventListener('click', (root as unknown as { _handleRootClick: (e: MouseEvent) => void })._handleRootClick);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventUrlId, rootElementId]);

  // When ticketQuantity or selectedDate changes, re-run markSelectedPerformance to update all custom-button-link hrefs
  useEffect(() => {
    const root = rootRef.current;
    if (root) {
      markSelectedPerformance(
        root,
        selectedPerformanceIdRef.current == null ? null : String(selectedPerformanceIdRef.current),
        (time) => onSelectedPerformanceTimeRef.current?.(time),
        () => setNavigating(true),
        ticketQuantity,
        selectedDate,
        eventUrlId,
        internalShowIdRef.current,
      );
    }
  }, [ticketQuantity, selectedDate, eventUrlId]);

  // Switching currency in the navbar doesn't mutate the widget's own DOM, so
  // the MutationObserver above never fires for it on its own — re-run the
  // rewrite directly whenever the user's selected currency changes. Also
  // re-runs once `pricingCoefficients` finishes loading (it starts `null`,
  // i.e. no markup, and the observer won't fire again on its own just
  // because a fetch resolved with no matching DOM mutation).
  useEffect(() => {
    const root = rootRef.current;
    if (root) {
      rewritePrices(
        root,
        (amount) => convertPrice(pricingCoefficients ? applyPricingCoefficients(pricingCoefficients, amount) : amount, 'GBP'),
        currencySymbol,
      );
    }
  }, [convertPrice, currencySymbol, pricingCoefficients]);

  /**
   * Host node for the mobile stepper portal — a `<div>` we prepend into the
   * vendor overlay's first child, matching `injectTicketCounter()`'s
   * `targetParent.insertAdjacentHTML('afterbegin', …)` placement in the CMS.
   * `null` whenever the overlay isn't up (desktop, or sheet closed).
   */
  const [overlayHost, setOverlayHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!forceTicketHeader || !showTicketHeader) {
      setOverlayHost(null);
      return;
    }
    const root = rootRef.current;
    if (!root) return;

    let host: HTMLDivElement | null = null;

    /** Attaches (or re-attaches) the host once the vendor's overlay exists.
     *  The overlay appears asynchronously after `toggle()`, and the vendor
     *  re-renders its subtree on month/date changes — which can detach our
     *  node — so this runs from a MutationObserver rather than once. Same
     *  reason the CMS drives `injectTicketCounter()` off its own
     *  `overlayObserver`. */
    function attach() {
      const overlay = root!.querySelector<HTMLElement>(
        '.widget-calendar-main-wrapper--overlay, [class*="calendar-wrapper--overlay"]',
      );
      const target = overlay?.firstElementChild as HTMLElement | undefined;
      if (!target) return;
      if (host && host.parentElement === target) return;

      if (!host) {
        host = document.createElement('div');
        host.className = 'shows-detail-ltd-ticket-header-portal';
      }
      target.prepend(host);
      setOverlayHost(host);
    }

    attach();
    const observer = new MutationObserver(attach);
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      host?.remove();
      setOverlayHost(null);
    };
  }, [forceTicketHeader, showTicketHeader]);

  function handleRetry() {
    setState('loading');
    initedForKeyRef.current = null;
    initWidget();
  }

  return (
    <div className="shows-detail-ltd-calendar-wrap">
      {/* Real full-page navigation to the seat-selection page, not a fetch — see
          `markSelectedPerformance`'s doc comment on `onNavigate`. Never explicitly reset back to
          `false`: a successful navigation unloads/unmounts this component entirely, same as the
          reference CMS's own `commonLoaderFunction(true)` before navigating away. */}
      {navigating && <ShowsLoadingOverlay label="Loading" />}
      {state === 'empty' && (
        <div className="shows-detail-ltd-calendar-empty" role="status">
          <i className="fa-regular fa-calendar-xmark" aria-hidden="true" />
          <p>Showtimes aren&apos;t available for this show right now.</p>
        </div>
      )}

      {state === 'error' && (
        <div className="shows-detail-ltd-calendar-error" role="alert">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
          <p>We couldn&apos;t load the showtimes calendar. Please try again.</p>
          <button type="button" className="shows-detail-ltd-calendar-retry" onClick={handleRetry}>
            Try again
          </button>
        </div>
      )}

      {state === 'loading' && <CalendarSkeleton />}

      {/* Matches the CMS's own timing (`injectTicketCounter()` only runs
          once the widget has actually rendered) — showing this above an
          empty/loading/error placeholder would read as interactive before
          there's anything to select tickets for. Also gated on `hasTable`
          (see its doc comment) so it doesn't float above blank space on a
          narrow viewport where the widget collapses to just its own
          sticky footer button. */}
      {state === 'ready' && hasTable && showTicketHeader && (
        <TicketQuantityHeader
          count={ticketQuantity}
          maxTicket={Math.max(1, maxTicket)}
          onChange={handleTicketQuantityChange}
        />
      )}

      {/* Mobile: the same stepper, portalled into the vendor's own full-screen
          overlay so it can't be painted over by it (the overlay is
          `position: fixed; z-index: 9999`). See `forceTicketHeader`. */}
      {overlayHost &&
        createPortal(
          <TicketQuantityHeader
            count={ticketQuantity}
            maxTicket={Math.max(1, maxTicket)}
            onChange={handleTicketQuantityChange}
            variant="overlay"
          />,
          overlayHost,
        )}

      {/* Vendor mount point — rendered whenever we have an `eventUrlId`,
          never conditionally unmounted, since the widget targets this
          exact element id imperatively. Hidden via CSS until content has
          actually appeared, so the vendor's own internal loading flicker
          never shows through before our skeleton hands off. */}
      {eventUrlId && (
        <div
          id={rootElementId}
          ref={rootRef}
          className="shows-detail-ltd-calendar"
          hidden={state !== 'ready'}
        />
      )}

      {eventUrlId && (
        <Script
          src={LTD_CALENDAR_SCRIPT_SRC}
          strategy="afterInteractive"
          onLoad={initWidget}
          onReady={initWidget}
          onError={() => setState('error')}
        />
      )}
    </div>
  );
}
